import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismIcon,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import HotelPricingAssistantDayDetailTabsCompsetRecommendationReasons from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/compset-recommendation-reasons';
import HotelPricingAssistantDayDetailTabsEventsAndHolidaysList from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/events-and-holidays-list';
import HotelPricingAssistantDayDetailTabsOccupancyRecommendationReasons from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/occupancy-recommendation-reasons';
import HotelPricingAssistantDayDetailTabsUserRulesRecommendationReasons from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/user-rules-recommendation-reasons';
import type { Carate, Carecommendation, Day, Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

interface HotelPricingAssistantDayDetailTabsDrawerOpenPricingDrawerSignature {
  Args: {
    date: string;
    dayModel: Day;
    periodOfSelectedDay: Period | undefined;
    selectedRoomForDrawer: string | null;
    toggleOpenPricingDrawer: (room: string | null) => void;
  };
}

export default class HotelPricingAssistantDayDetailTabsDrawerOpenPricingDrawer extends Component<HotelPricingAssistantDayDetailTabsDrawerOpenPricingDrawerSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @tracked hasOpeningAnimationFinished = this.isDrawerOpen ?? false;

  handleOpeningClosingAnimation = modifier(
    (element: unknown, [isDrawerOpen]: [boolean]) => {
      let timeout: ReturnType<typeof setTimeout>;

      if (isDrawerOpen) {
        // The opening animation takes 500ms, wait for it to be finished.
        // However, if we wait the same 500ms, it feels like there is a delay in displaying of contents, therefore, we reduce this waiting time by 100ms
        timeout = setTimeout(() => {
          this.hasOpeningAnimationFinished = true;
        }, 400);
      } else if (!isDrawerOpen) {
        this.hasOpeningAnimationFinished = false;
      }

      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      return () => {
        clearTimeout(timeout);
      };
    },
  );

  get carateObjectForSelectedRoom(): Carate | undefined {
    return this.args.periodOfSelectedDay?.carates?.find(
      (carate) =>
        carate.hotelinfo.id === this.hotelAndMarketService.hotel.hotelinfo.id &&
        carate.roomName === this.args.selectedRoomForDrawer,
    );
  }

  get carecommendation(): Carecommendation | null | undefined {
    return this.carateObjectForSelectedRoom?.carecommendation;
  }

  get isDrawerOpen(): boolean {
    return this.args.selectedRoomForDrawer !== null;
  }

  <template>
    <aside
      class="tab-open-pricing-drawer
        {{if this.isDrawerOpen 'tab-open-pricing-drawer--open'}}
        flex h-full flex-col"
      {{this.handleOpeningClosingAnimation this.isDrawerOpen}}
    >
      {{#if this.hasOpeningAnimationFinished}}
        <div
          class="tab-open-pricing-drawer__header flex flex-row items-center justify-between"
        >
          <div class="align-center flex flex-row gap-200">
            <PrismIcon
              @color="on-intelligence"
              @name="smart-insights"
              @size="medium"
            />
            <h4>{{t "smart_insights.title"}}</h4>
          </div>
          <PrismButton
            @buttonType="ghost"
            @icon="cross"
            @onClick={{fn @toggleOpenPricingDrawer null}}
          />
        </div>

        <h4
          class="tab-open-pricing-drawer__room-name p-400"
        >{{@selectedRoomForDrawer}}</h4>

        {{#if this.carecommendation}}
          <div class="tab-open-pricing-drawer__content h-full">
            <div
              class="tab-open-pricing-drawer__recommendations flex flex-col gap-200"
            >
              {{! Occupancy reasons }}
              {{#if
                this.carecommendation.paRecommendationReason.hasOccupancyReasons
              }}
                <HotelPricingAssistantDayDetailTabsOccupancyRecommendationReasons
                  @carecommendationOfSelectedDay={{this.carecommendation}}
                />
              {{/if}}

              {{! Compset reasons }}
              {{#if
                this.carecommendation.paRecommendationReason.hasCompsetReasons
              }}
                <HotelPricingAssistantDayDetailTabsCompsetRecommendationReasons
                  @carecommendationOfSelectedDay={{this.carecommendation}}
                  @date={{@date}}
                />
              {{/if}}

              {{! User rules reasons }}
              {{#if
                this.carecommendation.paRecommendationReason.hasUserRulesReasons
              }}
                <HotelPricingAssistantDayDetailTabsUserRulesRecommendationReasons
                  @carecommendationOfSelectedDay={{this.carecommendation}}
                />
              {{/if}}

              {{! Events and Holidays }}
              {{#if @dayModel}}
                <HotelPricingAssistantDayDetailTabsEventsAndHolidaysList
                  @dayModel={{@dayModel}}
                  @wrapDetails={{true}}
                />
              {{/if}}
            </div>
          </div>
        {{else}}
          <div class="flex items-center justify-center">
            <PrismMessage
              @description=""
              @illustration="100x100_success"
              @title={{t
                "pricing_assistant.no_recommendation.empty_state.title"
              }}
            />
          </div>
        {{/if}}
      {{/if}}
    </aside>
  </template>
}
